import { MapPin, Layers, Ruler, StickyNote, Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatCents } from '@/lib/utils';
import { SectionCard, DetailField, EmptyValue } from '@/components/SectionCard';
import type { CountertopJobFull } from '@/lib/api-types';

// ─── Field value helpers ─────────────────────────────────────────────────────

/** Render a stored comma-separated multi-select as compact chips. */
function Chips({ csv }: { csv: string | null }) {
  const items = (csv ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (items.length === 0) return <EmptyValue />;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span
          key={it}
          className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-xs"
        >
          {it}
        </span>
      ))}
    </div>
  );
}

function YesNo({ value }: { value: number | null | undefined }) {
  return value ? (
    <span className="font-medium text-foreground">Yes</span>
  ) : (
    <span className="text-muted-foreground">No</span>
  );
}

const fieldGrid = 'grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2';

// ─── Grouped intake cards ────────────────────────────────────────────────────

export function CountertopIntakeCards({
  job,
  onEditSqft,
  onEditFinalSqft,
  appointmentsSlot,
}: {
  job: CountertopJobFull;
  /** Opens the approx-sq.ft. modal (editable at any stage). */
  onEditSqft: () => void;
  /** Opens the final-sq.ft. modal (editable at any stage). */
  onEditFinalSqft: () => void;
  /** Appointments card, slotted between Site & Access and Material & Scope. */
  appointmentsSlot?: ReactNode;
}) {
  const sinkLabel =
    job.kitchen_sink === 'single'
      ? 'Yes — Single'
      : job.kitchen_sink === 'double'
        ? 'Yes — Double'
        : job.kitchen_sink === 'none'
          ? 'No'
          : 'Not specified';
  const edgeLabel = job.edge === 'double' ? 'Double' : job.edge === 'miter' ? 'Miter' : null;

  return (
    <div className="space-y-6">
      {/* Site & Access — address and access side by side */}
      <SectionCard icon={MapPin} title="Site & Access">
        <dl className={fieldGrid}>
          <DetailField label="Site address">
            {job.site_address ? (
              <span className="whitespace-pre-line">{job.site_address}</span>
            ) : (
              <EmptyValue />
            )}
          </DetailField>
          <DetailField label="Access">
            <Chips csv={job.access_detail} />
          </DetailField>
        </dl>
      </SectionCard>

      {/* Appointments (slotted from the detail page) */}
      {appointmentsSlot}

      {/* Material & Scope — now also carries Sink & Edge */}
      <SectionCard icon={Layers} title="Material & Scope">
        <dl className={fieldGrid}>
          <DetailField label="Material / stone" className="sm:col-span-2">
            {job.material_detail || <EmptyValue />}
          </DetailField>
          <DetailField label="Measurement areas" className="sm:col-span-2">
            <Chips csv={job.measurement_area} />
          </DetailField>
          <div className="grid grid-cols-3 gap-4 sm:col-span-2">
            <DetailField label="Removal">
              <YesNo value={job.removal} />
            </DetailField>
            <DetailField label="Garbage disposal">
              <YesNo value={job.garbage_disposal} />
            </DetailField>
            <DetailField label="Cooktop cutout">
              <YesNo value={job.cooktop_cutout} />
            </DetailField>
          </div>
          <DetailField label="Kitchen sink">{sinkLabel}</DetailField>
          <DetailField label="Edge">{edgeLabel ?? <EmptyValue />}</DetailField>
          {job.kitchen_sink === 'none' && (
            <DetailField label="Sink specification" className="sm:col-span-2">
              {job.sink_dimensions || (
                <span className="text-amber-700">Needed before measurement</span>
              )}
            </DetailField>
          )}
        </dl>
      </SectionCard>

      {/* Measurement & Quote */}
      <SectionCard icon={Ruler} title="Measurement & Quote">
        <dl className={fieldGrid}>
          <DetailField label="Approx sq.ft.">
            <span className="inline-flex items-center gap-2">
              {job.approx_sqft ? <span>{job.approx_sqft}</span> : <EmptyValue />}
              <button
                type="button"
                onClick={onEditSqft}
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Edit approx sq.ft."
                title="Edit approx sq.ft."
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </span>
          </DetailField>
          <DetailField label="Final sq.ft.">
            <span className="inline-flex items-center gap-2">
              {job.final_sqft ? (
                <span className="font-medium text-foreground">{job.final_sqft}</span>
              ) : (
                <EmptyValue />
              )}
              <button
                type="button"
                onClick={onEditFinalSqft}
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Edit final sq.ft."
                title="Edit final sq.ft."
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </span>
          </DetailField>
          <DetailField label="Approx quote">
            {job.approx_quote != null ? (
              <span className="tabular-nums">{formatCents(job.approx_quote)}</span>
            ) : (
              <EmptyValue />
            )}
          </DetailField>
        </dl>
      </SectionCard>

      {/* Notes */}
      <SectionCard icon={StickyNote} title="Notes">
        {job.notes ? (
          <p className="whitespace-pre-line text-sm text-foreground">{job.notes}</p>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
      </SectionCard>
    </div>
  );
}
